import { Combobox, Field, Option, Skeleton, SkeletonItem, Tooltip } from '@fluentui/react-components';
import { workItemTransitionsStyles } from '../styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAutomationRuleTrigger } from '../../use-trigger';
import { TriggerItemType, WorkItemTransitionErrorType, WorkItemTransitionTriggerType } from '../../types';
import useWorkItemTransition from '../use-work-item-transitions';
import { useAzureProvider } from 'src/azure/context';

interface Props {
  id?: string;
  validations: WorkItemTransitionErrorType['from'] | null
  data: WorkItemTransitionTriggerType;
	resetValidations(): void;
}

export default function ToValueInput(props: Props) {
	const classes = workItemTransitionsStyles();
	const {setTriggerData} = useAutomationRuleTrigger();
	const {applicationContext} = useAzureProvider();

	const [loading, setLoading] = useState(false);
	const [onInitialLoading, setOnInitialLoading] = useState(false);
	const [initialOptions, setInitialOptions] = useState<Array<{ value: string; label: string }>>([]);
	const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);

	const [status, setStatus] = useState<'searching' | 'selected'>('selected');
	const [text, setText] = useState<string>('');
  
	const workItemTransition = useWorkItemTransition();

	const onChangeText = useCallback((value: string) => {
		setStatus('searching');
		setText(value || '');

		if (value === '') {
			setOptions(initialOptions);
			setLoading(false);
		}

		if (value !== '') {
			setLoading(true);
			const filteredOptions = initialOptions.filter(item => item.label.toLowerCase().includes(value.toLowerCase()));
			setOptions(filteredOptions);
			setLoading(false);
		}
	}, [initialOptions, options]);

	const onSelectValue = useCallback((value: string) => {
		if (value) {
			props.resetValidations();
			setStatus('searching');
			setText('');

			setTriggerData((oldData) => {
				const data = oldData as TriggerItemType;
				const wiTransitionValues = data.workItemTransitions as WorkItemTransitionTriggerType;

				const values = wiTransitionValues.selectedTo.includes(value)
					? wiTransitionValues.selectedTo.filter(item => item !== value)
					: [...wiTransitionValues.selectedTo, value];

				return {
					...data,
					workItemTransitions: {
						...wiTransitionValues,
						selectedTo: values,
					}
				};
			});
		}
	}, []);

	const loadInitialOptions = useCallback(async () => {
		if (initialOptions.length > 0) {
			setOptions(initialOptions);
		}

		if (!initialOptions.length) {
			workItemTransition
				.getTransitionOptions({
					organizationName: applicationContext.organizationName,
					projectId: applicationContext.projectId,
				}).then((options) => {
					setInitialOptions(options);
					setOptions(options);
				});
		}
	}, [initialOptions]);

	const valueRender = useMemo(() => {
		if (props.data.selectedTo && props.data.selectedTo.length > 0 && status === 'selected') {
			return options.filter((item) => props.data.selectedTo.includes(item.value)).map((item) => item.label).join(', ');
		}

		if (text) {
			return text;
		}

		return '';
	}, [text, props.data.selectedTo, options, status]);

	const selectedOptions = useMemo(() => {
		const filteredValues = options.filter(item => props.data.selectedTo.includes(item.value));

		if (props.data && filteredValues.length > 0) {
			return filteredValues.map(item => item.value);
		}

		return [];
	}, [props.data.selectedTo, options]);

	useEffect(() => {
		setOnInitialLoading(true);
		workItemTransition.getTransitionOptions({
			organizationName: applicationContext.organizationName,
			projectId: applicationContext.projectId,
		}).then((options) => {
			setInitialOptions(options);
			setOptions(options);
		}).finally(() => {
			setOnInitialLoading(false);
		});
	}, []);

	if (onInitialLoading) {
		return (
			<Skeleton className={classes.skeletonRoot}>
				<SkeletonItem className={classes.skeletonItem} />
			</Skeleton>
		);
	}

	return (
		<>
			<Tooltip
				relationship='description'
				content={
					options
						.filter((item) => props.data.selectedTo?.includes(item.value))
						.map(item => item.label).join(', ') || 'No value'
				}
			>
				<Field
					validationMessage={props.validations}
				>
					<Combobox
						className={classes.dropdown}
						multiselect
						autoComplete='off'
						appearance='outline'
						placeholder='Choose values'
						positioning='below-start'
						selectedOptions={selectedOptions}
						onOptionSelect={(_, data) => {
							onSelectValue(data.optionValue as string);
						}}
						onChange={(ev) => {
							onChangeText(ev.target.value);
						}}
						onFocus={() => {
							setStatus('searching');
							setText('');
						}}
						onBlur={() => {
							setStatus('selected');
							loadInitialOptions();
						}}
						value={valueRender}
					>
						<div style={{maxHeight: 240}}>
							{!loading && options.map((item, idx) => (
								<Option key={idx} value={item.value}>{item.label}</Option>
							))}
							{loading && <Option value='loading'>Loading...</Option>}
						</div>
					</Combobox>
				</Field>
			</Tooltip>
		</>
	);
}